import { Group, TextInput, Box, Button, LoadingOverlay, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconRowRemove } from '@tabler/icons-react';
import { AppDataService } from '../../services/AppDataService';
import useInfoModal from '../../hooks/useInfoModal';
import { MODAL_TYPE_ENUM } from '../../enums/CommonEnums';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { CreateSubmissionFormValidations } from './validations/FormValidations';

// New component for Wine Considerations
const WineConsiderations = () => (
    <div>
        <h3>Beverages That Qualify As Wine</h3>
        <ul>
            <li>Table/Still Wine</li>
            <li>Sparkling Wine/Champagne</li>
            <li>Wine Spritzer</li>
            <li>Mead</li>
            <li>Sake</li>
            <li>Fortified Wine</li>
            <li>Cider (over 8.5% ABV)</li>
        </ul>
    </div>
);

const ContainerSubmissionForm = ({ onSubmit }) => {
    const openModal = useInfoModal();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Base beverage list WITHOUT Wine
    const baseBeverageTypes = [
        { value: 'Beer', label: 'Beer' },
        { value: 'Cider', label: 'Cider' },
        { value: 'Coffee', label: 'Coffee' },
        { value: 'Dairy', label: 'Dairy' },
        { value: 'Energy', label: 'Energy' },
        { value: 'Juice', label: 'Juice' },
        { value: 'Mixer', label: 'Mixer' },
        { value: 'Soda', label: 'Soda' },
        { value: 'Sports', label: 'Sports' },
        { value: 'Tea', label: 'Tea' },
        { value: 'Water', label: 'Water' },
    ];

    const form = useForm({
        initialValues: {
            entries: [
                { productName: '', beverageType: '', size: '', materialType: '', upc: '', color: '' }
            ],
        },
        validate: {
            entries: {
                productName: CreateSubmissionFormValidations.productName,
                beverageType: CreateSubmissionFormValidations.beverageType,
                size: CreateSubmissionFormValidations.size,
                materialType: CreateSubmissionFormValidations.materialType,
                upc: CreateSubmissionFormValidations.upc,
                color: CreateSubmissionFormValidations.color,
            },
        },
    });

    const handleFormSubmit = async (values) => {
        try {
            setLoading(true);
            const registrationEntries = form.values.entries;

            // Submit your data
            const webResp = await AppDataService.SubmitUserContainerRegistration({
                registrations: registrationEntries,
            });
            const responseData = webResp.getResponse();

            if (webResp.hasError()) {
                setLoading(false);
                return openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, responseData.error.message);
            }

            onSubmit(responseData.data.registrations);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
        }
    };

    // Render each row
    const fields = form.values.entries.map((entry, index) => {
        // Conditionally add "Wine" if this row's materialType is "Aluminum"
        const beverageData =
            entry.materialType === 'Aluminum'
                ? [...baseBeverageTypes, { value: 'Wine', label: 'Wine' }]
                : baseBeverageTypes;

        return (
            <Group key={index} mt="xs" spacing="xs" align="flex-start">
                <TextInput
                    maw={150}
                    size="xs"
                    placeholder="UPC/EAN"
                    {...form.getInputProps(`entries.${index}.upc`)}
                />
                <TextInput
                    maw={150}
                    size="xs"
                    placeholder="Product Brand & Name"
                    {...form.getInputProps(`entries.${index}.productName`)}
                />
                <Select
                    withinPortal
                    dropdownPosition="bottom"
                    size="xs"
                    placeholder="Material"
                    data={[
                        { value: 'Aluminum', label: 'Aluminum' },
                        { value: 'Plastic', label: 'Plastic' },
                        { value: 'Glass', label: 'Glass' },
                    ]}
                    onChange={(value) => {
                        // Update materialType
                        form.setFieldValue(`entries.${index}.materialType`, value);
                        // If the new material is NOT Aluminum and beverageType is Wine, reset beverageType
                        if (value !== 'Aluminum' && form.values.entries[index].beverageType === 'Wine') {
                            form.setFieldValue(`entries.${index}.beverageType`, '');
                        }
                    }}
                    value={form.values.entries[index].materialType}
                />
                <TextInput
                    maw={75}
                    size="xs"
                    placeholder="Size (oz)"
                    {...form.getInputProps(`entries.${index}.size`)}
                />
                <Select
                    withinPortal
                    dropdownPosition="bottom"
                    size="xs"
                    placeholder="Beverage Type"
                    data={beverageData}
                    onChange={(val) => {
                        form.setFieldValue(`entries.${index}.beverageType`, val);
                        if (val === 'Wine') {
                            // Delay the modal open slightly to ensure proper stacking
                            setTimeout(() => {
                                openModal(MODAL_TYPE_ENUM.GENERAL_INFO, <WineConsiderations />);
                            }, 0);
                        }
                    }}
                    value={form.values.entries[index].beverageType}
                />
                <Select
                    withinPortal
                    dropdownPosition="bottom"
                    size="xs"
                    placeholder="Color (if glass or plastic)"
                    data={[
                        { value: 'Amber', label: 'Amber' },
                        { value: 'Clear', label: 'Clear' },
                        { value: 'Green', label: 'Green' },
                    ]}
                    {...form.getInputProps(`entries.${index}.color`)}
                />
                {index > 0 && (
                    <Button
                        color="dark"
                        compact
                        onClick={() => form.removeListItem('entries', index)}
                    >
                        <IconRowRemove />
                    </Button>
                )}
            </Group>
        );
    });

    return (
        <Box mx="auto">
            <LoadingOverlay visible={loading} overlayBlur={2} />

            <div>{fields}</div>

            <Group position="center" mt="md">
                <Button
                    color="dark"
                    compact
                    onClick={() =>
                        form.insertListItem('entries', {
                            productName: '',
                            beverageType: '',
                            size: '',
                            materialType: '',
                            upc: '',
                            color: '',
                        })
                    }
                >
                    Add Record
                </Button>

                <Button color="dark" compact onClick={form.onSubmit(handleFormSubmit)}>
                    Submit
                </Button>

                <Button color="dark" compact onClick={() => form.reset()}>
                    Reset
                </Button>
            </Group>
        </Box>
    );
};

export default ContainerSubmissionForm;
